import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'

import { GET, POST, httpOk } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import SmsCatalogPicker from 'pages/parts/SmsCatalogPicker'
import SmsCounter from 'pages/parts/SmsCounter'

/**
 * SMS должникам: клиентам или организациям с долгом на выбранной точке.
 *
 * Сумма долга у каждого своя — её подставляет сервер ({debt_amount}). Шаг 1 — шаблон и поля,
 * шаг 2 — подтверждение: сколько получателей, общий долг, пример текста и стоимость;
 * отправка уходит в очередь. У своего провайдера заведения — свой текст с :debt_amount.
 */

const emptyCatalog = { template_code: null, vars: {}, locale: 'auto' }

function DebtorsSmsModal({ show, onHide, counterparty = 'client', posId = null }) {
	const { t } = useTranslation()

	const isOrganization = counterparty === 'organization'
	const [step, setStep] = useState('form')
	const [isSmsCatalog, setIsSmsCatalog] = useState(false)
	const [catalog, setCatalog] = useState({ ...emptyCatalog, locale: isOrganization ? 'ru' : 'auto' })
	const [message, setMessage] = useState('')
	const [minDebt, setMinDebt] = useState('')
	const [preview, setPreview] = useState(null)
	// Свой договор с оператором: свои шаблоны заведения типа «Долг клиента» (5) / «Долг организации» (6)
	const [ownTemplates, setOwnTemplates] = useState([])

	useEffect(() => {
		if (!show || isSmsCatalog) return

		async function loadOwnTemplates() {
			const response = await GET(`/helper/sms-templates?sms_type_id=${isOrganization ? 6 : 5}`)
			if (httpOk(response)) setOwnTemplates(response.data || [])
		}

		loadOwnTemplates()
	}, [show, isSmsCatalog, isOrganization])

	function close() {
		setStep('form')
		setCatalog({ ...emptyCatalog, locale: isOrganization ? 'ru' : 'auto' })
		setMessage('')
		setMinDebt('')
		setPreview(null)
		onHide()
	}

	function payload(isPreview) {
		const text = isSmsCatalog
			? { template_code: catalog.template_code, vars: catalog.vars, locale: catalog.locale }
			: { message }

		return {
			counterparty,
			pos_id: posId || undefined,
			min_debt: minDebt ? Number(String(minDebt).replace(/\s/g, '')) : undefined,
			...text,
			preview: isPreview ? 1 : undefined,
		}
	}

	const isReady = isSmsCatalog ? !!catalog.template_code : !!message.trim()

	async function previewSms(e) {
		e.preventDefault()
		if (!isReady) return
		const response = await POST('/sms/send-debtors', payload(true), { loader: true, notification: false })
		if (httpOk(response)) {
			setPreview(response.data)
			setStep('confirm')
		}
	}

	async function sendSms() {
		const response = await POST('/sms/send-debtors', payload(false), { loader: true })
		if (httpOk(response)) close()
	}

	return (
		<Modal show={show} animation={false} centered scrollable size="lg" onHide={close}>
			<Modal.Header closeButton>
				<Modal.Title>{isOrganization ? t('sms_debtors_organizations') : t('sms_debtors_clients')}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: '560px' }}>
				{step === 'form' &&
					<form id="debtorsSmsForm" onSubmit={previewSms} autoComplete="off">
						<SmsCatalogPicker
							usage={isOrganization ? 'debt_org' : 'debt'}
							value={catalog}
							onChange={setCatalog}
							onModeChange={setIsSmsCatalog}
							allowGuestLocale={!isOrganization}
						/>

						{!isSmsCatalog && ownTemplates.length > 0 &&
							<div className="form-group">
								<label>{t('sms_templates')}</label>
								<Select
									options={ownTemplates}
									onChange={(option) => setMessage(option ? option.text : '')}
									isClearable
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						}
						{!isSmsCatalog &&
							<div className="form-group">
								<label>{t('text')}<span className="required-mark">*</span></label>
								<textarea className="form-control" rows="5" maxLength={1000}
									value={message}
									onChange={(e) => setMessage(e.target.value)} />
								<div className="text-muted fz-12">{t('sms_debtors_own_text_hint')}</div>
								<SmsCounter text={message} />
							</div>
						}

						<div className="form-group">
							<label>{t('sms_debtors_min_debt')}</label>
							<input type="number" className="form-control" min="0"
								value={minDebt}
								onChange={(e) => setMinDebt(e.target.value)} />
						</div>
					</form>
				}

				{step === 'confirm' && preview &&
					<div>
						<div className="mb-2">
							{t('sms_debtors_recipients')}: <b>{formatMoney(preview.recipients_count, 0)}</b>
						</div>
						<div className="mb-2">
							{t('sms_debtors_total_debt')}: <b>{formatMoney(preview.total_debt, 0)}</b>
						</div>
						<div className="mb-2 text-muted">{t('sms_segments')}: {preview.segments}</div>
						{preview.estimated_cost !== null && preview.estimated_cost !== undefined &&
							<div className="mb-2">
								{t('sms_debtors_cost')}: <b>{formatMoney(preview.estimated_cost, 0)}</b>
								<span className="text-muted"> ({formatMoney(preview.price, 0)} × {preview.segments} · {t('balance')} {formatMoney(preview.balance, 0)})</span>
							</div>
						}
						{preview.estimated_cost !== null && preview.estimated_cost > preview.balance &&
							<div className="alert alert-warning py-2">{t('sms_debtors_low_balance')}</div>
						}
						<label className="mt-2">{t('sms_debtors_example')}</label>
						<div className="p-2" style={{ background: 'var(--dark-surface-2, #f6f8fa)', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
							{preview.text}
						</div>
					</div>
				}
			</Modal.Body>
			<Modal.Footer>
				<div className="d-flex gap-2 w-100">
					{step === 'form' &&
						<>
							<button type="button" className="btn btn-outline-warning w-100" onClick={close}>{t('cancel')}</button>
							<button type="submit" form="debtorsSmsForm" className="btn btn-primary w-100" disabled={!isReady}>{t('sms_debtors_check')}</button>
						</>
					}
					{step === 'confirm' &&
						<>
							<button type="button" className="btn btn-outline-secondary w-100" onClick={() => setStep('form')}>{t('back')}</button>
							<button type="button" className="btn btn-primary w-100" onClick={sendSms}>
								{t('send')} {formatMoney(preview?.recipients_count, 0)} SMS
							</button>
						</>
					}
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default DebtorsSmsModal
